import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowUpRight,
    MapPin,
    Briefcase,
    Clock,
    ShieldCheck,
    Building,
} from "lucide-react";
import { careersApi } from "@/lib/careersApi";
import { COMPANY } from "@/lib/company";
import ShareMenu from "@/components/careers/ShareMenu";
import PageSEO from "@/components/seo/PageSEO";

function formatDeadline(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function CareersPage() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        careersApi
            .listJobs()
            .then((d) => {
                if (!cancelled) setJobs(d);
            })
            .catch(() => {
                if (!cancelled) setError("Sorry — we couldn't load our current openings. Please try again shortly.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div data-testid="careers-page" className="pb-24">
            <PageSEO
                title="Construction Jobs Manchester — Sponsored UK Skilled Worker Roles | EB Construction"
                description={`Now hiring ${jobs.length || 8} construction professionals in Manchester. £41,000+ salaries. UK Home Office licensed sponsor for the Skilled Worker route. Apply online today.`}
                canonicalPath="/careers"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "CollectionPage",
                    url: "https://elevatebuildconstruction.co.uk/careers",
                    name: "EB Construction Careers",
                    description:
                        "Open positions at EB Construction, Manchester. UK Home Office Skilled Worker sponsorship available on flagged roles.",
                    hasPart: jobs.map((j) => ({
                        "@type": "JobPosting",
                        title: j.title,
                        url: `https://elevatebuildconstruction.co.uk/careers/${j.slug}`,
                        employmentType: "FULL_TIME",
                        hiringOrganization: {
                            "@id": "https://elevatebuildconstruction.co.uk/#organization",
                        },
                    })),
                }}
            />
            {/* Hero band */}
            <section
                className="relative isolate overflow-hidden bg-brand-charcoal text-brand-offwhite"
                data-testid="careers-hero"
            >
                <div className="pointer-events-none absolute -right-24 -top-16 select-none font-industrial text-[200px] leading-none text-white/[0.05] md:text-[300px]">
                    CAREERS
                </div>
                <div className="container-x grid gap-10 py-20 md:grid-cols-12 md:py-28">
                    <div className="md:col-span-7">
                        <p
                            className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold"
                            data-testid="careers-hero-eyebrow"
                        >
                            Open positions · {COMPANY.address.city}, {COMPANY.address.region}
                        </p>
                        <span className="rule-gold mt-3" aria-hidden="true" />
                        <motion.h1
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="mt-4 font-display text-5xl font-semibold leading-[1.05] md:text-6xl lg:text-7xl"
                            data-testid="careers-hero-title"
                        >
                            Build your career with{" "}
                            <span className="accent-italic text-brand-gold">ElevateBuild.</span>
                        </motion.h1>
                        <p
                            className="mt-6 max-w-2xl font-body text-base leading-relaxed text-brand-offwhite/85 md:text-lg"
                            data-testid="careers-hero-copy"
                        >
                            We're a Companies House-registered UK contractor operating out of Manchester,
                            delivering residential, commercial and civils projects across England. We are a{" "}
                            <span className="font-semibold text-brand-gold">
                                UK Home Office Licensed Sponsor
                            </span>{" "}
                            (Skilled Worker route) and welcome applications from candidates with the right to
                            work in the UK — and from overseas candidates who need sponsorship.
                        </p>
                        <div className="mt-8 border-l-2 border-brand-gold bg-white/[0.03] p-6">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                                Sponsorship
                            </p>
                            <p className="mt-3 flex items-start gap-2 font-body text-sm leading-relaxed text-brand-offwhite/85">
                                <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold" />
                                We hold an active UK Home Office sponsor licence. Roles flagged{" "}
                                <span className="font-mono text-brand-gold">Sponsor</span> are open to
                                Skilled Worker visa applicants.
                            </p>
                            <p className="mt-4 flex items-start gap-2 font-body text-sm leading-relaxed text-brand-offwhite/85">
                                <Building className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold" />
                                Companies House {COMPANY.companyNumber} · Founded{" "}
                                {COMPANY.foundedDisplay}.
                            </p>
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <div className="relative h-64 w-full overflow-hidden border border-brand-linelight md:h-full md:min-h-[520px]">
                            <img
                                src="/brand/careers-portrait.jpg"
                                alt="ElevateBuild site team at work in Manchester"
                                className="h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/40 via-transparent to-transparent" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Jobs listing */}
            <section className="bg-brand-cream pt-16">
                <div className="container-x">
                <div className="mb-8 flex items-end justify-between gap-4">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                            Currently hiring
                        </p>
                        <span className="rule-gold" aria-hidden="true" />
                        <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.1] text-brand-charcoal md:text-5xl">
                            Open roles
                        </h2>
                    </div>
                    <p className="hidden font-mono text-xs text-brand-steel/70 md:block">
                        {loading ? "" : `${jobs.length} position${jobs.length === 1 ? "" : "s"}`}
                    </p>
                </div>

                {error && (
                    <div className="border border-rose-500/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-800">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="grid gap-4 md:grid-cols-2" data-testid="careers-skeleton">
                        {[0, 1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="h-40 animate-pulse border border-brand-line bg-white/50"
                            />
                        ))}
                    </div>
                ) : jobs.length === 0 ? (
                    <div
                        className="border border-dashed border-brand-line bg-white/50 p-10 text-center"
                        data-testid="careers-empty"
                    >
                        <p className="font-display text-2xl uppercase tracking-wide text-brand-charcoal">
                            No positions open right now
                        </p>
                        <p className="mt-2 font-body text-sm text-brand-steel">
                            Check back soon — we're a growing team. In the meantime, you can register your
                            interest via our{" "}
                            <Link to="/contact" className="text-brand-gold underline-offset-2 hover:underline">
                                contact page
                            </Link>
                            .
                        </p>
                    </div>
                ) : (
                    <ul className="grid gap-4 md:grid-cols-2" data-testid="careers-jobs-grid">
                        {jobs.map((job, i) => (
                            <motion.li
                                key={job.id}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "0px 0px -80px 0px" }}
                                transition={{ duration: 0.4, delay: i * 0.05 }}
                                className="relative"
                            >
                                <Link
                                    to={`/careers/${job.slug}`}
                                    className="group flex h-full flex-col justify-between border border-brand-line bg-white p-6 transition-all hover:-translate-y-1 hover:border-brand-gold"
                                    data-testid={`careers-job-card-${job.slug}`}
                                >
                                    <div>
                                        <div className="mb-3 flex flex-wrap items-center gap-2 pr-10">
                                            {job.sponsorship_available && (
                                                <span className="border border-brand-gold px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-gold">
                                                    Sponsor
                                                </span>
                                            )}
                                            <span className="border border-brand-line px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-steel">
                                                {job.department}
                                            </span>
                                        </div>
                                        <h3 className="font-display text-xl font-semibold leading-tight text-brand-charcoal transition-colors group-hover:text-brand-gold md:text-2xl">
                                            {job.title}
                                        </h3>
                                        <ul className="mt-4 flex flex-col gap-1.5 font-body text-sm text-brand-steel">
                                            <li className="flex items-center gap-2">
                                                <MapPin className="h-3.5 w-3.5 text-brand-gold" />
                                                {job.location}
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <Briefcase className="h-3.5 w-3.5 text-brand-gold" />
                                                {job.employment_type}
                                                {job.salary_range ? ` · ${job.salary_range}` : ""}
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <Clock className="h-3.5 w-3.5 text-brand-gold" />
                                                Apply by {formatDeadline(job.deadline)}
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="mt-6 flex items-center justify-between border-t border-brand-line pt-4">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-charcoal group-hover:text-brand-gold">
                                            Read more & apply
                                        </span>
                                        <ArrowUpRight className="h-4 w-4 text-brand-charcoal transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-gold" />
                                    </div>
                                </Link>
                                <div className="absolute right-4 top-4 z-10">
                                    <ShareMenu
                                        job={job}
                                        url={
                                            typeof window !== "undefined"
                                                ? `${window.location.origin}/careers/${job.slug}`
                                                : `/careers/${job.slug}`
                                        }
                                        variant="icon"
                                        testIdPrefix={`job-card-share-${job.slug}`}
                                        menuTestId={`job-card-share-menu-${job.slug}`}
                                        anchor="right"
                                    />
                                </div>
                            </motion.li>
                        ))}
                    </ul>
                )}
                </div>
            </section>
        </div>
    );
}
